import { sheets } from '@/app/grid/controller/Sheets';
import type { Sheet } from '@/app/grid/sheet/Sheet';
import { intersects } from '@/app/gridGL/helpers/intersects';
import { HtmlCellResizing } from '@/app/gridGL/HTMLGrid/htmlCells/HtmlCellResizing';
import { content } from '@/app/gridGL/pixiApp/contentInstance';
import { pixiApp } from '@/app/gridGL/pixiApp/PixiApp';
import type { Wheel } from '@/app/gridGL/pixiApp/viewport/Wheel';
import { isMobileMode } from '@/app/helpers/isMobile';
import type { JsHtmlOutput } from '@/app/quadratic-core-types';
import { CELL_HEIGHT, CELL_WIDTH } from '@/shared/constants/gridConstants';
import { Point, Rectangle, type FederatedPointerEvent } from 'pixi.js';

// Import lazily to avoid circular dependency - returns undefined if not ready
const getMobileChartPlaceholders = () => content.mobileChartPlaceholders;

// number of screen pixels to trigger the resize cursor
const tolerance = 5;

// this can be removed since we lock the width and height (I think?)

// this should be kept in sync with run_code/mod.rs and aiToolsSpec.ts
export const DEFAULT_HTML_WIDTH = 600;
const DEFAULT_HTML_HEIGHT = 460;

export const DEFAULT_HTML_CELL_WIDTH = Math.ceil(DEFAULT_HTML_WIDTH / CELL_WIDTH);
export const DEFAULT_HTML_CELL_HEIGHT = Math.ceil(DEFAULT_HTML_HEIGHT / CELL_HEIGHT);

export class HtmlCell {
  private right: HTMLDivElement;
  private bottom: HTMLDivElement;
  private resizing: HtmlCellResizing | undefined;
  private hoverSide: 'right' | 'bottom' | 'corner' | undefined;
  private offset: Rectangle;

  // cache for the thumbnail image
  private thumbnailImage: Promise<string | undefined> | undefined;

  // pre-generated chart image from server (base64 WebP data URL)
  private chartImage: string | undefined;

  // whether the cell is active
  active = false;

  // whether pointer events are allowed on the iframe (currently when selected
  // but not resizing)
  pointerEvents: 'auto' | 'none' = 'none';

  border: HTMLDivElement;

  htmlCell: JsHtmlOutput;

  // bounds for all grid cells that are part of this html cell
  gridBounds: Rectangle;

  sheet: Sheet;

  div: HTMLDivElement;
  iframe: HTMLIFrameElement | undefined;

  // whether we're in mobile mode (checked once at construction)
  private isMobile: boolean;

  // whether the iframe has been loaded (for mobile lazy loading)
  private iframeLoaded = false;

  private autoResizeTimeout: NodeJS.Timeout | undefined;
  private abortController = new AbortController();

  // Token for the currently-pending deferred mobile iframe load. Used so that
  // rapid activate/deactivate cycles can cancel an in-flight load before it
  // actually attaches an iframe to the DOM.
  private pendingMobileLoad = 0;

  constructor(htmlCell: JsHtmlOutput) {
    if (htmlCell.html === null) throw new Error('Expected html to be defined in HtmlCell constructor');
    this.htmlCell = htmlCell;
    const sheet = sheets.getById(htmlCell.sheet_id)!;
    if (!sheet) {
      throw new Error(`Expected to find sheet with id ${htmlCell.sheet_id}`);
    }
    this.sheet = sheet;
    this.isMobile = isMobileMode();

    this.div = document.createElement('div');
    this.div.className = 'html-cell';

    this.offset = this.sheet.getScreenRectangle(this.x, this.adjustedY, this.htmlCell.w, this.htmlCell.h - 1);
    this.div.style.left = `${this.offset.x}px`;
    this.div.style.top = `${this.offset.y}px`;
    this.div.style.width = `${this.offset.width}px`;
    this.div.style.height = `${this.offset.height}px`;

    this.right = document.createElement('div');
    this.right.className = 'html-resize-control-right';
    const topHeight = this.sheet.offsets.getRowHeight(this.y);
    this.right.style.top = `-${topHeight}px`;
    this.right.style.height = `calc(100% + ${topHeight}px)`;

    this.bottom = document.createElement('div');
    this.bottom.className = 'html-resize-control-bottom';

    this.border = document.createElement('div');
    this.border.className = 'w-full h-full absolute top-0 left-0';
    this.border.style.border = '1px solid hsl(var(--muted-foreground))';

    // Store pre-generated chart image if available
    this.chartImage = htmlCell.chart_image ?? undefined;

    this.div.append(this.right);

    // On mobile, don't create any DOM elements - MobileChartPlaceholders handles rendering via Pixi
    // The iframe will be created when the cell is activated
    if (this.isMobile) {
      this.iframeLoaded = false;
      // Hide the div since Pixi handles rendering; it will be shown when activated
      this.div.style.display = 'none';
    } else {
      // Desktop: Create and load iframe immediately
      this.createIframeElement();
      this.iframe!.srcdoc = htmlCell.html;
      this.div.append(this.iframe!);
      this.iframeLoaded = true;

      if (this.iframe!.contentWindow?.document.readyState === 'complete') {
        this.afterLoad();
      }
      this.iframe!.addEventListener('load', this.afterLoad);
    }

    this.div.append(this.bottom);
    this.div.append(this.border);
    this.gridBounds = new Rectangle(this.x, this.y, this.htmlCell.w - 1, this.htmlCell.h - 1);

    if (this.sheet.id !== sheets.current || !this.isWithinClampBounds()) {
      this.div.style.visibility = 'hidden';
    }
  }

  destroy() {
    this.pendingMobileLoad++;
    this.abortController.abort();
    this.div.remove();
  }

  get x(): number {
    return Number(this.htmlCell.x);
  }
  get adjustedY(): number {
    return Number(this.htmlCell.y) + 1;
  }

  get y(): number {
    return Number(this.htmlCell.y);
  }

  get width(): number {
    return this.offset.width;
  }
  get height(): number {
    return this.offset.height;
  }

  isOutputEqual(htmlCell: JsHtmlOutput): boolean {
    return (
      this.htmlCell.sheet_id === htmlCell.sheet_id && htmlCell.x === this.htmlCell.x && htmlCell.y === this.htmlCell.y
    );
  }

  private afterLoad = () => {
    if (this.iframe?.contentWindow) {
      // forward the wheel event to the pixi viewport and adjust its position
      this.iframe.contentWindow.document.removeEventListener('wheel', this.handleWheel);
      this.iframe.contentWindow.document.addEventListener('wheel', this.handleWheel, { passive: false });
      this.iframe.contentWindow.document.body.style = 'margin: 0';

      // Show the iframe now that margin is set (was hidden during mobile load)
      if (this.iframe.style.opacity === '0') {
        this.iframe.style.opacity = '1';
        // Now safe to hide the Pixi placeholder
        if (this.isMobile) {
          getMobileChartPlaceholders()?.setActivated(this.sheet.id, BigInt(this.x), BigInt(this.y), true);
        }
      }

      this.recalculateBounds();

      // Generate chart image if missing (for older files without pre-generated PNGs)
      if (!this.chartImage) {
        this.generateChartImage();
      }
    } else if (this.iframe) {
      throw new Error('Expected content window to be defined on iframe');
    }
  };

  private generateChartImage = () => {
    if (!this.iframe) return;
    const plotly = (this.iframe.contentWindow as any)?.Plotly;
    const plotElement = this.iframe.contentWindow?.document.querySelector('.js-plotly-plot');
    if (plotly && plotElement) {
      // Snapshot the abort controller and iframe so we can detect destruction
      // (or iframe swap) before the async toImage resolves. Without this, the
      // .then() can mutate chartImage on a stale instance after the cell has
      // been torn down or replaced.
      const abortController = this.abortController;
      const iframe = this.iframe;
      plotly
        .toImage(plotElement, {
          // Safari's canvas.toDataURL('image/webp') silently falls back to PNG,
          // so request PNG explicitly to keep MIME types accurate cross-browser.
          format: 'png',
          width: this.width,
          height: this.height,
        })
        .then((dataUrl: string) => {
          if (abortController.signal.aborted) return;
          if (this.iframe !== iframe) return;
          this.chartImage = dataUrl;
        })
        .catch((error: unknown) => {
          console.error('[HtmlCell] Failed to generate chartImage:', error);
        });
    }
  };

  private handleWheel = (event: WheelEvent) => {
    event.stopPropagation();
    event.preventDefault();
    const viewport = pixiApp.viewport;
    const wheel = viewport.plugins.get('wheel') as Wheel | null;
    if (!wheel) {
      throw new Error('Expected wheel plugin to be defined on viewport');
    }
    const bounding = this.div.getBoundingClientRect();
    wheel.wheel(event, {
      x: bounding.left + event.clientX * viewport.scale.x - event.clientX,
      y: bounding.top + event.clientY * viewport.scale.y - event.clientY,
    });
  };

  update(htmlCell: JsHtmlOutput) {
    if (!htmlCell.html) throw new Error('Expected html to be defined in HtmlCell.update');

    const htmlChanged = htmlCell.html !== this.htmlCell.html;

    // Update chart image
    this.chartImage = htmlCell.chart_image ?? undefined;

    // On mobile without active iframe: MobileChartPlaceholders handles updates via events
    // If HTML changed but we're not showing iframe, we'll load fresh iframe on activate
    if (this.isMobile && !this.iframeLoaded) {
      // Nothing to do here - Pixi layer handles it
    } else if (htmlChanged && this.iframe) {
      // If the HTML content has changed, create a new iframe and swap it to avoid flickering
      const newIframe = document.createElement('iframe');
      newIframe.className = 'html-cell-iframe';
      newIframe.style.pointerEvents = 'none';
      newIframe.srcdoc = htmlCell.html;
      newIframe.setAttribute('border', '0');
      newIframe.setAttribute('scrolling', 'no');
      newIframe.style.backgroundColor = '#fff';

      // Hide the new iframe while it loads
      newIframe.style.opacity = '0';
      newIframe.style.position = 'absolute';
      newIframe.style.top = '0';
      newIframe.style.left = '0';

      // Capture old iframe reference for cleanup
      const oldIframe = this.iframe;

      // Set up the load handler to swap the iframes
      const onLoad = () => {
        // Remove the old iframe and clean up its event listeners
        if (oldIframe.contentWindow?.document) {
          oldIframe.contentWindow.document.removeEventListener('wheel', this.handleWheel);
        }
        oldIframe.removeEventListener('load', this.afterLoad);

        // Make the new iframe visible and reset styles
        newIframe.style.opacity = '1';
        newIframe.style.position = '';
        newIframe.style.top = '';
        newIframe.style.left = '';

        // Remove the old iframe and update reference
        oldIframe.remove();
        this.iframe = newIframe;

        // Set up the new iframe
        this.afterLoad();
      };

      newIframe.addEventListener('load', onLoad, { once: true });

      // Add the new iframe to the DOM so it can load (hidden initially)
      this.div.appendChild(newIframe);
    }

    this.htmlCell = htmlCell;
    this.gridBounds = new Rectangle(this.x, this.y, this.htmlCell.w - 1, this.htmlCell.h - 1);
    this.thumbnailImage = undefined;
    this.abortController.abort();
    this.abortController = new AbortController();
    // recalculateBounds updates the div/iframe/border sizes and re-runs autoResize
    // so Plotly relayouts when the chart is resized externally (e.g. by the AI).
    this.recalculateBounds();
  }

  private isWithinClampBounds(): boolean {
    const cellClampBounds = this.sheet.getCellClampBounds();
    if (!cellClampBounds) return true;
    return intersects.rectangleRectangle(this.gridBounds, cellClampBounds);
  }

  private updateVisibility(sheetId: string) {
    this.div.style.visibility = sheetId === this.htmlCell.sheet_id && this.isWithinClampBounds() ? 'visible' : 'hidden';
  }

  changeSheet(sheetId: string) {
    this.updateVisibility(sheetId);
  }

  isSheet(sheetId: string) {
    return sheetId === this.htmlCell.sheet_id;
  }

  hover(e: FederatedPointerEvent): 'right' | 'bottom' | 'corner' | 'body' | undefined {
    const target = this.intersects(e);
    if (target === 'right' || target === 'bottom' || target === 'corner') {
      this.hoverSide = target;
      this.highlightEdge();
    } else if (this.hoverSide) {
      this.hoverSide = undefined;
      this.clearHighlightEdges();
    }
    return target;
  }

  private intersects(e: FederatedPointerEvent): 'right' | 'bottom' | 'corner' | 'body' | undefined {
    const rect = this.div.getBoundingClientRect();
    const { left, top } = pixiApp.canvas.getBoundingClientRect();
    const viewport = pixiApp.viewport;
    const toleranceScaled = tolerance * viewport.scale.x;
    const pointerX = e.global.x + left;
    const pointerY = e.global.y + top;
    const right = pointerX - rect.right < toleranceScaled && pointerX - rect.right > 0;
    const bottom = Math.abs(pointerY - rect.bottom) < toleranceScaled;
    if (right && bottom) {
      return 'corner';
    }
    if (right && pointerY > rect.top && pointerY < rect.bottom) {
      return 'right';
    }
    if (bottom && pointerX > rect.left && pointerX < rect.right) {
      return 'bottom';
    }
    if (pointerX > rect.left && pointerX < rect.right && pointerY > rect.top && pointerY < rect.bottom) {
      return 'body';
    }
  }

  clearHighlightEdges() {
    this.right.style.backgroundColor = '';
    this.bottom.style.backgroundColor = '';
    this.right.classList.remove('html-resize-control-right-corner');
    this.bottom.classList.remove('html-resize-control-bottom-corner');
  }

  highlightEdge() {
    const side = this.hoverSide;

    if (side === 'right' || side === 'corner') {
      this.right.style.backgroundColor = 'hsl(var(--primary))';
    }
    if (side === 'bottom' || side === 'corner') {
      this.bottom.style.backgroundColor = 'hsl(var(--primary))';
    }

    if (side === 'corner') {
      this.right.classList.add('html-resize-control-right-corner');
      this.bottom.classList.add('html-resize-control-bottom-corner');
    }
  }

  pointerMove(world: Point) {
    if (!this.resizing) {
      throw new Error('Expected resizing to be defined in HtmlCell.pointerMove');
    }
    this.resizing.pointerMove(world);
  }

  startResizing() {
    if (!this.hoverSide) {
      throw new Error('Expected hoverSide to be defined in HtmlCell.startResizing');
    }
    if (this.iframe) {
      this.iframe.style.pointerEvents = 'none';
    }
    this.resizing = new HtmlCellResizing(
      this,
      this.hoverSide,
      this.iframe ? parseInt(this.iframe.width) : this.width,
      this.iframe ? parseInt(this.iframe.height) : this.height
    );
  }

  cancelResizing() {
    if (!this.resizing) {
      throw new Error('Expected resizing to be defined in HtmlCell.endResizing');
    }
    this.right.classList.remove('html-resize-control-right-corner');
    this.bottom.classList.remove('html-resize-control-bottom-corner');
    this.resizing.cancelResizing();
    this.resizing = undefined;
    if (this.iframe) {
      this.iframe.style.pointerEvents = this.pointerEvents;
    }
  }

  completeResizing() {
    if (!this.resizing) {
      throw new Error('Expected resizing to be defined in HtmlCell.endResizing');
    }
    this.resizing.completeResizing();
    this.resizing = undefined;
    if (this.iframe) {
      this.iframe.style.pointerEvents = this.pointerEvents;
    }
  }

  setWidth(width: number) {
    if (this.iframe) {
      this.iframe.width = width.toString();
    }
    this.border.style.width = `${width}px`;
  }

  setHeight(height: number) {
    if (this.iframe) {
      this.iframe.height = height.toString();
    }
    this.border.style.height = `${height}px`;
  }

  private recalculateBounds() {
    this.offset = this.sheet.getScreenRectangle(
      this.x,
      this.adjustedY,
      this.htmlCell.w ?? 0,
      (this.htmlCell.h ?? 0) - 1
    );
    this.div.style.left = `${this.offset.x}px`;
    this.div.style.top = `${this.offset.y}px`;
    this.div.style.width = `${this.offset.width}px`;
    this.div.style.height = `${this.offset.height}px`;
    if (this.iframe) {
      this.iframe.width = this.width.toString();
      this.iframe.height = this.height.toString();
    }
    this.border.style.width = `${this.width}px`;
    this.border.style.height = `${this.height}px`;

    const topHeight = this.sheet.offsets.getRowHeight(this.y);
    this.right.style.top = `-${topHeight}px`;
    this.right.style.height = `calc(100% + ${topHeight}px)`;

    this.updateVisibility(sheets.current);
    this.autoResize();
  }

  updateOffsets() {
    this.recalculateBounds();
  }

  // checks if the cell contains a grid point
  contains(x: number, y: number): boolean {
    return intersects.rectanglePoint(this.gridBounds, new Point(x, y));
  }

  activate() {
    this.active = true;
    this.border.style.border = '2px solid hsl(var(--primary))';
    this.pointerEvents = 'auto';

    // On mobile, show the div and create/load iframe when activated
    // The Pixi placeholder will be hidden in afterLoad once the iframe is ready
    if (this.isMobile) {
      // Show the div container
      this.div.style.display = '';
      this.updateVisibility(sheets.current);

      // Defer iframe creation by two animation frames so that any iframe
      // removed from a different HtmlCell in the same tick (e.g. when
      // swapping between active charts) has time to be torn down by the
      // browser before we begin loading the next one. This is critical
      // for mobile memory: two Plotly iframes alive simultaneously will
      // OOM low-memory devices.
      if (!this.iframeLoaded) {
        const token = ++this.pendingMobileLoad;
        requestAnimationFrame(() =>
          requestAnimationFrame(() => {
            if (token !== this.pendingMobileLoad) return;
            if (!this.active || this.iframeLoaded) return;
            this.loadIframe();
          })
        );
      }
    }

    if (this.iframe) {
      this.iframe.style.pointerEvents = this.pointerEvents;
    }
  }

  private loadIframe() {
    if (this.iframeLoaded) return;

    // Create the iframe hidden - it will be shown in afterLoad once margin is set
    if (!this.iframe) {
      this.createIframeElement(true);
    }

    // Attach the load listener before inserting into the DOM so we can't
    // miss the load event between insertion and listener attachment.
    this.iframe!.addEventListener('load', this.afterLoad, { once: true });
    this.iframe!.srcdoc = this.htmlCell.html!;
    this.iframeLoaded = true;

    this.div.insertBefore(this.iframe!, this.bottom);
  }

  private createIframeElement(hidden = false) {
    this.iframe = document.createElement('iframe');
    this.iframe.className = 'html-cell-iframe';
    this.iframe.style.pointerEvents = 'none';
    this.iframe.width = this.width.toString();
    this.iframe.height = this.height.toString();
    this.iframe.setAttribute('border', '0');
    this.iframe.setAttribute('scrolling', 'no');
    this.iframe.style.backgroundColor = '#fff';
    if (hidden) {
      this.iframe.style.opacity = '0';
    }
  }

  private unloadIframe() {
    // Cancel any deferred mobile load that hasn't executed yet
    this.pendingMobileLoad++;

    if (!this.iframeLoaded || !this.isMobile || !this.iframe) return;

    const iframe = this.iframe;
    this.iframe = undefined;
    this.iframeLoaded = false;

    if (iframe.contentWindow?.document) {
      iframe.contentWindow.document.removeEventListener('wheel', this.handleWheel);
    }
    iframe.removeEventListener('load', this.afterLoad);

    // Navigate the iframe to about:blank before removing it. This forces the
    // browser to synchronously tear down the previous document (Plotly,
    // scripts, DOM, and their JS heap) rather than waiting for GC on the
    // detached iframe. Critical for avoiding mobile OOM when swapping charts.
    try {
      iframe.removeAttribute('srcdoc');
      iframe.src = 'about:blank';
    } catch {
      // ignore - fall through to remove()
    }
    iframe.remove();
  }

  temporarilyDeactivate() {
    this.border.style.border = '1px solid hsl(var(--muted-foreground))';
    this.pointerEvents = 'none';
    if (this.iframe) {
      this.iframe.style.pointerEvents = this.pointerEvents;
    }
  }

  deactivate() {
    this.active = false;
    this.border.style.border = '1px solid hsl(var(--muted-foreground))';
    this.pointerEvents = 'none';
    if (this.iframe) {
      this.iframe.style.pointerEvents = this.pointerEvents;
    }

    // On mobile, hide the div and unload iframe to save memory
    // Pixi layer will show the placeholder sprite
    if (this.isMobile) {
      this.unloadIframe();
      this.div.style.display = 'none';

      // Tell Pixi layer to show the placeholder sprite
      getMobileChartPlaceholders()?.setActivated(this.sheet.id, BigInt(this.x), BigInt(this.y), false);
    }
  }

  reactivate() {
    if (this.active) {
      this.activate();
    }
  }

  private autoResize = () => {
    if (this.autoResizeTimeout) {
      clearTimeout(this.autoResizeTimeout);
      this.autoResizeTimeout = undefined;
    }

    if (!this.iframe) return;

    if (this.iframe.contentWindow) {
      const plotly = (this.iframe.contentWindow as any).Plotly;
      const plotElement = this.iframe.contentWindow.document.querySelector('.js-plotly-plot');
      if (plotly && plotElement) {
        plotly.relayout(plotElement, {
          width: this.width,
          height: this.height,
        });
      }
    } else {
      this.autoResizeTimeout = setTimeout(this.autoResize, 100);
      this.abortController.signal.addEventListener(
        'abort',
        () => {
          clearTimeout(this.autoResizeTimeout);
          this.autoResizeTimeout = undefined;
        },
        { once: true }
      );
    }
  };

  getImageDataUrl = (): Promise<string | undefined> => {
    // Use pre-generated chart image if available
    if (this.chartImage) {
      return Promise.resolve(this.chartImage);
    }

    if (this.thumbnailImage) {
      return this.thumbnailImage;
    }

    // If no iframe exists (mobile deferred), return undefined
    if (!this.iframe) {
      return Promise.resolve(undefined);
    }

    if (this.iframe.contentWindow?.document.readyState === 'complete') {
      this.thumbnailImage = this.getImageDataUrlAfterLoaded(this.abortController);
    } else {
      this.thumbnailImage = new Promise((resolve) => {
        this.iframe!.addEventListener(
          'load',
          () => {
            this.getImageDataUrlAfterLoaded(this.abortController)
              .then(resolve)
              .catch((error) => {
                console.error('[HtmlCell.ts] getImageDataUrlAfterLoaded:', error);
                resolve(undefined);
              });
          },
          { once: true, signal: this.abortController.signal }
        );
      });
    }

    return this.thumbnailImage;
  };

  private getImageDataUrlAfterLoaded = (abortController: AbortController): Promise<string | undefined> => {
    return new Promise((resolve) => {
      abortController.signal.addEventListener(
        'abort',
        () => {
          resolve(undefined);
        },
        { once: true }
      );

      if (!this.iframe) {
        resolve(undefined);
        return;
      }

      const plotly = (this.iframe.contentWindow as any)?.Plotly;
      const plotElement = this.iframe.contentWindow?.document.querySelector('.js-plotly-plot');
      if (!plotly || !plotElement) {
        resolve(undefined);
      } else {
        plotly
          .toImage(plotElement, {
            format: 'png',
            width: this.width,
            height: this.height,
          })
          .then((dataUrl: string) => {
            resolve(dataUrl);
          })
          .catch((error: any) => {
            console.error('[HtmlCell.ts] getImageDataUrlAfterLoaded:', error);
            resolve(undefined);
          });
      }
    });
  };
}
